import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import {
  DEMO_LOT_EVENT,
  DEMO_LOT_OFFSET,
  isDemoLotActive,
} from "./demoLotMode";
import {
  SUPERADMIN_MODE_EVENT,
  SUPERADMIN_MODE_STORAGE_KEY,
  isSuperadminModeEnabled,
} from "../SuperadminHeader";
import { useWalkthrough } from "../Walkthrough/WalkthroughContext";
import "./DemoLotBanner.css";

const isBrowser = typeof window !== "undefined";

const DemoLotBanner: React.FC = () => {
  const location = useLocation();
  const [demoActive, setDemoActive] = useState<boolean>(() => isDemoLotActive());
  const [superadminOn, setSuperadminOn] = useState<boolean>(() =>
    isSuperadminModeEnabled()
  );
  const [hidden, setHidden] = useState<boolean>(false);
  const { active: walkthroughActive, start: startWalkthrough, end: endWalkthrough } = useWalkthrough();

  useEffect(() => {
    if (!isBrowser) return undefined;

    const handleDemoChange = (event: Event) => {
      const detail = (event as CustomEvent<{ active: boolean }>).detail;
      setDemoActive(detail.active);
    };

    const handleSuperadminChange = (event: Event) => {
      const detail = (event as CustomEvent<{ enabled: boolean }>).detail;
      setSuperadminOn(detail.enabled);
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === SUPERADMIN_MODE_STORAGE_KEY) {
        setSuperadminOn(event.newValue === "true");
      }
    };

    window.addEventListener(DEMO_LOT_EVENT, handleDemoChange as EventListener);
    window.addEventListener(SUPERADMIN_MODE_EVENT, handleSuperadminChange as EventListener);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener(DEMO_LOT_EVENT, handleDemoChange as EventListener);
      window.removeEventListener(SUPERADMIN_MODE_EVENT, handleSuperadminChange as EventListener);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    setHidden(false);
  }, [demoActive, superadminOn, location.pathname]);

  const isLoginRoute = location.pathname.startsWith("/login");
  const shouldRender = demoActive && !superadminOn && !isLoginRoute && !hidden;

  useEffect(() => {
    if (typeof document === "undefined") return;

    const { body } = document;

    if (shouldRender) {
      body.classList.add("demo-lot-mode");
      body.style.setProperty("--superadmin-offset", `${DEMO_LOT_OFFSET}px`);
    } else {
      body.classList.remove("demo-lot-mode");
      if (!isSuperadminModeEnabled()) {
        body.style.removeProperty("--superadmin-offset");
      }
    }

    return () => {
      body.classList.remove("demo-lot-mode");
      if (!isSuperadminModeEnabled()) {
        body.style.removeProperty("--superadmin-offset");
      }
    };
  }, [shouldRender]);

  if (!shouldRender || typeof document === "undefined") {
    return null;
  }

  const bannerContent = (
    <div className="demo-lot-banner" role="status" aria-live="polite">
      <div className="demo-lot-banner-left">
        <span className="demo-lot-banner-label">Demo Lot</span>
        <a
          className="demo-lot-banner-button"
          href="https://www.parkwithparallel.com/contact"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src="/assets/demo/inquire.svg" alt="" aria-hidden="true" />
          <span>Inquire Now</span>
        </a>
        <a
          className="demo-lot-banner-button"
          href="https://www.parkwithparallel.com/demos/operator"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src="/assets/demo/video.svg" alt="" aria-hidden="true" />
          <span>Demo Video</span>
        </a>
        {walkthroughActive ? (
          <button
            type="button"
            className="demo-lot-banner-button demo-lot-banner-button--end"
            onClick={() => endWalkthrough(true)}
          >
            <span
              className="demo-icon-end"
              aria-hidden="true"
              style={{
                WebkitMaskImage: "url(/assets/demo/end_walkthrough.svg)",
                maskImage: "url(/assets/demo/end_walkthrough.svg)",
              }}
            />
            <span>End Walkthrough</span>
          </button>
        ) : (
          <button
            type="button"
            className="demo-lot-banner-button"
            onClick={() => startWalkthrough()}
          >
            <img src="/assets/demo/begin_walkthrough.svg" alt="" aria-hidden="true" />
            <span>Begin Walkthrough</span>
          </button>
        )}
      </div>
      <button
        type="button"
        className="demo-lot-banner-hide"
        onClick={() => setHidden(true)}
        aria-label="Hide demo lot banner"
      >
        <img src="/assets/x.svg" alt="" aria-hidden="true" />
        <span>Hide</span>
      </button>
    </div>
  );

  return createPortal(bannerContent, document.body);
};

export default DemoLotBanner;
